import { mutationOptions, queryOptions } from '@tanstack/react-query';
import { trackError } from '@proposify/frontend-observability';
import { variablesBffClient } from 'services/api';
import { Endpoints } from 'utils/endpoints';
import { QueryKeys } from 'utils/queryKeys';
import type { ContentLink, FlexDataLink, RelatedLink } from './types';

export const toFlexDataLink = (contentLink: ContentLink): FlexDataLink => ({
  dataSourceType: contentLink.toObject.dataSourceType,
  tenantId: contentLink.toObject.tenantId,
  objectType: contentLink.toObject.objectType,
  objectTypeLabel: contentLink.toObject.objectType,
  normalizedType: contentLink.normalizedType,
  objectId: contentLink.toObject.objectId,
  objectLabel: contentLink.toObject.objectLabel,
  qualifier: contentLink.qualifier,
  status: contentLink.status,
  createdAt: contentLink.createdAt,
});

type SearchCombination = { dataSourceType: string; tenantId: string; objectType: string };

export const linkQueries = {
  search: (searchTerm: string, combinations: SearchCombination[]) =>
    queryOptions({
      queryKey: [QueryKeys.searchFlexDataLinks, searchTerm, combinations],
      queryFn: (): Promise<FlexDataLink[]> =>
        Promise.all(
          combinations.map(({ dataSourceType, tenantId, objectType }) =>
            variablesBffClient
              .get<{ data: FlexDataLink[] }>(Endpoints.searchFlexDataLinks, {
                params: { dataSourceType, tenantId, objectType, keywords: searchTerm },
              })
              .then((response) => response.data)
          )
        ).then((results) => results.flat()),
      enabled: combinations.length > 0 && searchTerm.length >= 3,
      throwOnError: (error: Error) => {
        trackError(error);
        return false;
      },
    }),
  list: (contentId: string) =>
    queryOptions({
      queryKey: QueryKeys.getContentFlexDataLinks(contentId),
      queryFn: () => variablesBffClient.get<ContentLink[]>(Endpoints.getFlexDataLinks(contentId)),
      enabled: !!contentId,
      throwOnError: (error: Error) => {
        trackError(error);
        return false;
      },
    }),
  link: (contentId: string) =>
    mutationOptions({
      mutationFn: (obj: FlexDataLink) =>
        variablesBffClient.post<{
          toObject: ContentLink['toObject'];
          relatedLinks?: RelatedLink[];
        }>(Endpoints.flexDataLink(contentId), {
          dataSourceType: obj.dataSourceType,
          tenantId: obj.tenantId,
          objectType: obj.objectType,
          objectId: obj.objectId,
          qualifier: obj.qualifier,
          createRelatedLinks: obj.normalizedType === 'Opportunity',
        }),
    }),
  unlink: (contentId: string) =>
    mutationOptions({
      mutationFn: (obj: FlexDataLink) =>
        variablesBffClient.deleteWithData(Endpoints.flexDataLink(contentId), {
          dataSourceType: obj.dataSourceType,
          tenantId: obj.tenantId,
          objectType: obj.objectType,
          objectId: obj.objectId,
          qualifier: obj.qualifier,
        }),
    }),
};
